import { makeAutoObservable, runInAction } from "mobx";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { RootStore } from "../../shared/stores/RootStore";
import { LifeCycleStage } from "../models/LifeCycleStage";
import { OsaWayApi } from "../services/OsaWayApi";
import { CreateOsaWay, OsaWay, UpdateOsaWay } from "../models/OsaWay";
import { CampaignApi } from "../../project/services/CampaignApi";

export const defaultOsaWayScopes = [
  OptionalScope.Stats,
  OptionalScope.GeoJson,
  OptionalScope.Description,
  OptionalScope.PlannedTrajectory,
  OptionalScope.ScoreImpact,
  OptionalScope.Score,
  OptionalScope.SafetyTreatmentModel
];

export class OsaWayStore {
  rootStore;
  ways: OsaWay[] = [];
  selectedId?: string;

  pageSize = 4;
  nextPage: number | null = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.ways = [];
      this.selectedId = undefined;
      this.nextPage = 0;
    });
  }

  get(osaWayId: string): OsaWay | undefined {
    const { filterStore } = this.rootStore;
    const ways = filterStore.originalOrForecastedOsaWays;
    return ways.find((way) => way.id === osaWayId);
  }

  getSelected() {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  select(osaWayId: string) {
    const { hlaStore } = this.rootStore;
    hlaStore.clearSelected();

    runInAction(() => (this.selectedId = osaWayId));
  }

  clearSelected() {
    const { osaNodeStore } = this.rootStore;
    osaNodeStore.clearSelected();
    runInAction(() => (this.selectedId = undefined));
  }

  async refreshOsaWay(osaWayId: string) {
    const { data: way } = await OsaWayApi.get(osaWayId, defaultOsaWayScopes);
    runInAction(() => {
      this.ways = this.ways.map((r) => (r.id === osaWayId ? way : r));
    });
  }

  async fetchAllWays(campaignId: string, cancelIf?: () => boolean) {
    runInAction(() => {
      this.ways = [];
      this.nextPage = 0;
    });
    while (this.nextPage !== null) {
      const response = await CampaignApi.pageOsaWays(
        campaignId,
        defaultOsaWayScopes,
        this.pageSize,
        this.nextPage
      );
      if (cancelIf?.()) return;
      runInAction(() => {
        this.ways = this.ways.concat(response.data.data);
        this.nextPage = response.data.meta.nextPage;
      });
    }
  }

  async fetchAndSelectOsaWay(osaWayId: string) {
    const { data: way } = await OsaWayApi.get(osaWayId, defaultOsaWayScopes);
    runInAction(() => {
      this.ways = [way];
      this.select(way.id);
    });
  }

  async deleteOsaWay(id: string) {
    await OsaWayApi.delete(id);
    runInAction(() => {
      this.ways = this.ways.filter((way) => way.id !== id);
    });
  }

  async createOsaWay(request: CreateOsaWay) {
    const { data: createdOsaWay } = await OsaWayApi.create(
      request,
      defaultOsaWayScopes
    );

    const { campaignStore } = this.rootStore;
    if (campaignStore.selectedId) {
      await this.fetchAllWays(campaignStore.selectedId);
      this.select(createdOsaWay.id);
    }
    return createdOsaWay;
  }

  async updateOsaWay(
    osaWayId: string,
    request: UpdateOsaWay,
    scopes: OptionalScope[] = defaultOsaWayScopes
  ) {
    const { data: updatedWay } = await OsaWayApi.update(
      osaWayId,
      request,
      scopes
    );
    runInAction(() => {
      this.ways = this.ways.map((way) =>
        way.id === osaWayId ? updatedWay : way
      );
    });
    return updatedWay;
  }

  async updateLifeCycleStage(osaWayId: string, lifeCycleStage: LifeCycleStage) {
    await OsaWayApi.updateLifeCycleStage(
      osaWayId,
      lifeCycleStage,
      defaultOsaWayScopes
    ).then(({ data: updatedWay }) => {
      runInAction(() => {
        this.ways = this.ways.map((way) =>
          way.id === osaWayId ? updatedWay : way
        );
      });
    });
  }

  async compileOsaWayNodes(osaWayId: string) {
    const { data: updatedWay } = await OsaWayApi.compileNodes(
      osaWayId,
      defaultOsaWayScopes
    );

    runInAction(() => {
      this.ways = this.ways.map((way) =>
        way.id === osaWayId ? updatedWay : way
      );
    });
  }
}
